import { useState, useEffect, useCallback, useRef } from 'react';
import type { AvatarComposition } from '../types/types';
import type { UserAvatarSave } from '../services/avatarSaveService';
import { loadUserAvatarForEditing } from '../services/avatarSaveService';
import { useAppStore } from '../../../store';

interface UseLoadUserAvatarReturn {
  avatarComposition: AvatarComposition;
  avatarData: UserAvatarSave | null;
  hasExistingAvatar: boolean;
  isLoading: boolean;
  error: string | null;
  reloadAvatar: () => Promise<void>;
}

export const useLoadUserAvatar = (userId?: string): UseLoadUserAvatarReturn => {
  const [avatarComposition, setAvatarComposition] = useState<AvatarComposition>([]);
  const [avatarData, setAvatarData] = useState<UserAvatarSave | null>(null);
  const [hasExistingAvatar, setHasExistingAvatar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  
  const { user } = useAppStore();
  
  // Capture user ID in a ref so loadAvatar doesn't re-trigger on every user change
  const userIdRef = useRef(user?._id);
  useEffect(() => {
    // Only update the ref if we don't have a user ID yet (first load)
    if (!userIdRef.current && user?._id) {
      userIdRef.current = user._id;
    }
  }, [user?._id]);
  
  // Track if initial load has been done
  const hasLoadedRef = useRef(false);

  const loadAvatar = useCallback(async () => {
    // Skip reload if already loaded once (prevents refreshUser from wiping composition)
    if (hasLoadedRef.current) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      
      const targetUserId = userId || userIdRef.current;
      
                  
      if (!targetUserId) {
              }
      
      const result = await loadUserAvatarForEditing(targetUserId);
      
      setAvatarComposition(result.avatarComposition);
      setAvatarData(result.avatarData);
      setHasExistingAvatar(result.hasExistingAvatar);
      
      if (result.hasExistingAvatar) {
                      } else {
                      }
      
      hasLoadedRef.current = true;
      
    } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro desconhecido ao carregar avatar');
      
      
      setAvatarComposition([]);
      setAvatarData(null);
      setHasExistingAvatar(false);
      
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  
  useEffect(() => {
    loadAvatar();
  }, [loadAvatar]);

  // If user ID wasn't available on first render, retry once when it becomes available
  useEffect(() => {
    if (user?._id && !hasLoadedRef.current) {
      userIdRef.current = user._id;
      loadAvatar();
    }
  }, [user?._id, loadAvatar]);

  return {
    avatarComposition,
    avatarData,
    hasExistingAvatar,
    isLoading,
    error,
    reloadAvatar: loadAvatar
  };
};